import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Button from '../../components/ui/Button/Button';
import Input from '../../components/ui/Input/Input';
import Spinner from '../../components/ui/Spinner/Spinner';
import { getIncubation, updateIncubation } from '../../controllers/incubations';
import { useToast } from '../../contexts/ToastContext';
import { useUser } from '../../contexts/UserContext';
import './SurveysScreen.css';

const normalizeSurveys = (list) =>
  (Array.isArray(list) ? list : []).map((s) => ({
    name: s?.name || '',
    url: s?.url || '',
    results_url: s?.results_url || ''
  }));

const formatSurveyName = (name) => {
  const base = (name || '').trim();
  if (!base) return 'Untitled Survey';
  return base.toLowerCase().includes('survey') ? base : `${base} Survey`;
};

const SurveysScreen = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { showSuccess, showError } = useToast();
  const { hasIncubationPermission } = useUser();

  const initialIncubation = location.state?.incubation || null;
  const incubationId =
    location.state?.incubationId ||
    initialIncubation?.id ||
    null;

  const [incubation, setIncubation] = useState(initialIncubation);
  const [loading, setLoading] = useState(!initialIncubation);
  const [surveys, setSurveys] = useState(
    normalizeSurveys(initialIncubation?.surveys)
  );

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create' | 'edit'
  const [modalIndex, setModalIndex] = useState(null);
  const [draftName, setDraftName] = useState('');
  const [draftUrl, setDraftUrl] = useState('');
  const [draftResultsUrl, setDraftResultsUrl] = useState('');
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState(false);

  const canEdit = incubationId
    ? hasIncubationPermission(incubationId, 'edit_incubations')
    : false;

  useEffect(() => {
    if (!incubationId) {
      setLoading(false);
      return;
    }
    if (initialIncubation) return;

    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const response = await getIncubation({ incubation_id: incubationId });
        if (cancelled) return;
        if (response?.data?.success && response.data.incubation) {
          const inc = response.data.incubation;
          setIncubation(inc);
          setSurveys(normalizeSurveys(inc.surveys));
        } else {
          showError(response?.data?.error || 'Failed to load incubation.');
        }
      } catch (err) {
        if (cancelled) return;
        showError(err?.message || 'Failed to load incubation.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [incubationId, initialIncubation, showError]);

  const handleBack = () => navigate(-1);

  const openCreateModal = () => {
    setModalMode('create');
    setModalIndex(null);
    setDraftName('');
    setDraftUrl('');
    setDraftResultsUrl('');
    setModalOpen(true);
  };

  const openEditModal = (index) => {
    if (!canEdit) return;
    const s = surveys[index];
    if (!s) return;
    setModalMode('edit');
    setModalIndex(index);
    setDraftName(s.name || '');
    setDraftUrl(s.url || '');
    setDraftResultsUrl(s.results_url || '');
    setModalOpen(true);
  };

  const closeModal = () => {
    if (saving || removing) return;
    setModalOpen(false);
    setModalIndex(null);
    setDraftName('');
    setDraftUrl('');
    setDraftResultsUrl('');
  };

  const persistSurveys = async (nextSurveys) => {
    const response = await updateIncubation({
      incubation_id: incubationId,
      surveys: nextSurveys
    });
    if (!response?.data?.success) {
      throw new Error(response?.data?.error || 'Failed to save surveys.');
    }
    return nextSurveys;
  };

  const handleSave = async () => {
    if (saving) return;
    const name = draftName.trim();
    const url = draftUrl.trim();
    const results_url = draftResultsUrl.trim();
    if (!name) {
      showError('Survey name is required.');
      return;
    }
    if (!url) {
      showError('Survey URL is required.');
      return;
    }

    const nextSurveys = [...surveys];
    const surveyData = { name, url, results_url };
    if (modalMode === 'edit' && modalIndex !== null) {
      nextSurveys[modalIndex] = surveyData;
    } else {
      nextSurveys.push(surveyData);
    }

    try {
      setSaving(true);
      const saved = await persistSurveys(nextSurveys);
      setSurveys(saved);
      showSuccess(modalMode === 'edit' ? 'Survey updated.' : 'Survey added.');
      setModalOpen(false);
      setModalIndex(null);
      setDraftName('');
      setDraftUrl('');
      setDraftResultsUrl('');
    } catch (err) {
      showError(err?.message || 'Failed to save survey.');
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async () => {
    if (removing || saving) return;
    if (modalMode !== 'edit' || modalIndex === null) return;

    const confirmed = window.confirm('Remove this survey? This cannot be undone.');
    if (!confirmed) return;

    const nextSurveys = surveys.filter((_, i) => i !== modalIndex);
    try {
      setRemoving(true);
      const saved = await persistSurveys(nextSurveys);
      setSurveys(saved);
      showSuccess('Survey removed.');
      setModalOpen(false);
      setModalIndex(null);
      setDraftName('');
      setDraftUrl('');
      setDraftResultsUrl('');
    } catch (err) {
      showError(err?.message || 'Failed to remove survey.');
    } finally {
      setRemoving(false);
    }
  };

  const renderHeader = (title, subtitle) => (
    <div className="sv-topbar">
      <div className="sv-topbar-left">
        <button className="sv-back-btn" onClick={handleBack} aria-label="Back">
          <i className="fas fa-arrow-left"></i>
        </button>
        <div className="sv-topbar-title-group">
          <span className="sv-topbar-eyebrow">Incubation</span>
          <div className="sv-topbar-title-row">
            <h1 className="sv-topbar-title">{title}</h1>
            {subtitle && <span className="sv-id-chip">{subtitle}</span>}
          </div>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="surveys-screen">
        {renderHeader('Surveys')}
        <div className="sv-state">
          <Spinner full size="lg" label="Loading surveys..." />
        </div>
      </div>
    );
  }

  if (!incubationId || !incubation) {
    return (
      <div className="surveys-screen">
        {renderHeader('Surveys')}
        <div className="sv-state sv-error">
          <i className="fas fa-exclamation-triangle" style={{ fontSize: '24px', marginBottom: '16px' }}></i>
          <p>Missing incubation context. Please return to the incubation list.</p>
        </div>
      </div>
    );
  }

  const title = incubation?.name || 'Surveys';
  const shareableChip = incubation?.shareable_id
    ? String(incubation.shareable_id).toUpperCase()
    : '';

  return (
    <div className="surveys-screen">
      {renderHeader(title, shareableChip)}

      <div className="sv-content">
        <section className="sv-section">
          <div className="sv-section-header">
            <div className="sv-section-heading">
              <div className="sv-section-title-row">
                <h2 className="sv-section-title">Surveys</h2>
                <span className="sv-count-badge">{surveys.length}</span>
              </div>
              <p className="sv-section-sub">
                Click a survey to edit its name or URL. Surveys with a name and URL can be sent to participants.
              </p>
            </div>
            {canEdit && (
              <Button
                variant="primary"
                size="small"
                className="sv-header-action-btn"
                onClick={openCreateModal}
                disabled={saving || removing}
              >
                <i className="fas fa-plus"></i>
                Add Survey
              </Button>
            )}
          </div>
          <div className="sv-section-body">
            {surveys.length === 0 ? (
              <div className="sv-empty">
                {canEdit
                  ? 'No surveys configured yet. Click "Add Survey" to create one.'
                  : 'No surveys configured yet.'}
              </div>
            ) : (
              <ul className="sv-surveys-list">
                {surveys.map((survey, index) => (
                  <li
                    key={`${index}-${survey.name}`}
                    className={`sv-survey-row${canEdit ? ' is-clickable' : ''}`}
                    onClick={canEdit ? () => openEditModal(index) : undefined}
                    role={canEdit ? 'button' : undefined}
                    tabIndex={canEdit ? 0 : undefined}
                    onKeyDown={canEdit ? (e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        openEditModal(index);
                      }
                    } : undefined}
                  >
                    <div className="sv-survey-info">
                      <div className="sv-survey-name">{formatSurveyName(survey.name)}</div>
                      {survey.url && (
                        <div className="sv-survey-url">{survey.url}</div>
                      )}
                      {survey.results_url && (
                        <a
                          className="sv-survey-results-link"
                          href={survey.results_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <i className="fas fa-external-link-alt"></i>
                          View Results
                        </a>
                      )}
                    </div>
                    {canEdit && <i className="fas fa-chevron-right sv-survey-chevron"></i>}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>
      </div>

      {modalOpen && (
        <div
          className="sv-modal-overlay"
          role="dialog"
          aria-modal="true"
          onClick={closeModal}
        >
          <div className="sv-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="sv-modal-header">
              <h3 className="sv-modal-title">
                {modalMode === 'edit' ? 'Edit Survey' : 'Add Survey'}
              </h3>
              <button
                className="sv-modal-close"
                onClick={closeModal}
                aria-label="Close"
                disabled={saving || removing}
              >
                ×
              </button>
            </div>
            <div className="sv-modal-body">
              <div className="sv-modal-field">
                <label className="sv-modal-field-label" htmlFor="sv-survey-name">
                  Survey Name
                </label>
                <Input
                  id="sv-survey-name"
                  type="text"
                  placeholder="e.g. Onboarding"
                  value={draftName}
                  onChange={(e) => setDraftName(e.target.value)}
                  className="form-input"
                  disabled={saving || removing}
                />
              </div>
              <div className="sv-modal-field">
                <label className="sv-modal-field-label" htmlFor="sv-survey-url">
                  Survey URL
                </label>
                <Input
                  id="sv-survey-url"
                  type="text"
                  placeholder="https://..."
                  value={draftUrl}
                  onChange={(e) => setDraftUrl(e.target.value)}
                  className="form-input"
                  disabled={saving || removing}
                />
              </div>
              <div className="sv-modal-field">
                <label className="sv-modal-field-label" htmlFor="sv-survey-results-url">
                  Survey Results URL
                  <span className="sv-modal-field-optional">(optional)</span>
                </label>
                <Input
                  id="sv-survey-results-url"
                  type="text"
                  placeholder="https://docs.google.com/..."
                  value={draftResultsUrl}
                  onChange={(e) => setDraftResultsUrl(e.target.value)}
                  className="form-input"
                  disabled={saving || removing}
                />
              </div>
            </div>
            <div className="sv-modal-footer">
              {modalMode === 'edit' && (
                <Button
                  variant="outline"
                  size="small"
                  className="sv-modal-delete-btn"
                  onClick={handleRemove}
                  disabled={saving || removing}
                  loading={removing}
                >
                  <i className="fas fa-trash"></i>
                  {removing ? 'Removing...' : 'Remove'}
                </Button>
              )}
              <div className="sv-modal-footer-spacer" />
              <Button
                variant="outline"
                size="small"
                className="sv-modal-cancel-btn"
                onClick={closeModal}
                disabled={saving || removing}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="small"
                className="sv-modal-save-btn"
                onClick={handleSave}
                disabled={saving || removing || !draftName.trim() || !draftUrl.trim()}
                loading={saving}
              >
                {saving ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SurveysScreen;
